/* Overlay focus: while a drawer or modal is open, Tab and Shift+Tab stay inside it; when it closes,
   focus goes back to whatever opened it (or to a fallback, when the opener was not focusable). */

import { RefObject, useEffect } from "react";

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function useDialogFocus(ref: RefObject<HTMLElement>, active: boolean, fallback?: () => HTMLElement | null) {
  useEffect(() => {
    if (!active) return;
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      const box = ref.current;
      if (e.key !== "Tab" || !box) return;
      const items = Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1], cur = document.activeElement as HTMLElement | null;
      const inside = !!cur && box.contains(cur);
      if (e.shiftKey && (!inside || cur === first)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (!inside || cur === last)) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      // a click on plain content focuses its tabindex=-1 container (the page), which is no use to come back to
      const usable = !!opener && opener !== document.body && opener.getAttribute("tabindex") !== "-1" && document.contains(opener);
      const back = usable ? opener : fallback ? fallback() : null;
      // after the overlay has gone, so the browser doesn't move focus again
      setTimeout(() => back?.focus(), 0);
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
}
